<template>
    <div class="TicketForm">
        <DashboardEditSection title="Information">
            <FormNumber
                v-if="isEditMode"
                v-model="ticketNumber"
                label="Ticket #"
                disabled
            />

            <FormDateTimeInput
                v-if="isEditMode"
                v-model="createdAt"
                label="Date Submitted"
                disabled
            />

            <FormDateTimeInput
                v-model="submittedAt"
                label="Ticket Date"
            />

            <FormSelectDriver
                v-model.number="$v.user.$model"
                :errors="$v.user.$error ? [
                    !$v.user.required && 'Driver is required',
                ] : null"
            />
            <FormInput
                v-if="isEditMode"
                v-model="client"
                label="Client"
                disabled
            />
            <FormSelectProject
                v-model.number="$v.project.$model"
                :errors="$v.project.$error ? [
                    !$v.project.required && 'Project is required',
                ] : null"
            />
            <FormSelectVehicle
                v-model.number="$v.truck.$model"
                label="Truck"
                type="trucks"
                :errors="$v.truck.$error ? [
                    !$v.truck.required && 'Truck is required',
                ] : null"
            />
            <FormSelectVehicle
                v-model.number="trailer"
                label="Trailer"
                type="trailers"
            />

            <FormTextarea v-model="notes" label="Ticket Notes"/>
        </DashboardEditSection>

        <DashboardEditSection title="Pre Trip">
            <FormNumber
                v-model="$v.odometerPrecheck.$model"
                label="Pre Trip Odometer"
                :min="0"
                :errors="$v.odometerPrecheck.$error ? [
                    !$v.odometerPrecheck.required && 'Pre Trip Odometer is required',
                ] : null"
            />
            <Row>
                <Column :md="6">
                    <FormDateTimeInput
                        v-model="precheckStartAt"
                        label="Start Pre Trip"
                        :errors="$v.precheckStartAt.$error ? [
                            !$v.precheckStartAt.required && 'Start Pre Trip is required',
                        ] : null"
                    />
                </Column>
                <Column :md="6">
                    <FormDateTimeInput
                        v-model="$v.yardLeaveAt.$model"
                        label="Leave Yard"
                        :errors="$v.yardLeaveAt.$error ? [
                            !$v.yardLeaveAt.required && 'Leave Yard is required',
                            !$v.yardLeaveAt.minValue && 'Leave Yard has to be after Start Pre Trip',
                        ] : null"
                    />
                </Column>
            </Row>
        </DashboardEditSection>

        <DashboardEditSection title="Loads">
            <TicketLoadList :loads.sync="loads" @change="$emit('loadChanged', $event)"/>
        </DashboardEditSection>

        <DashboardEditSection title="Post Trip">
            <Row>
                <Column :md="6">
                    <FormDateTimeInput
                        v-model="$v.yardArriveAt.$model"
                        label="Arrive Yard"
                        :errors="$v.yardArriveAt.$error ? [
                            !$v.yardArriveAt.required && 'Arrive Yard is required',
                            !$v.yardArriveAt.minValue && 'Arrive Yard has to be after Leave Yard',
                        ] : null"
                    />
                </Column>
                <Column :md="6">
                    <FormDateTimeInput
                        v-model="$v.postcheckEndAt.$model"
                        label="Complete Post Trip"
                        :errors="$v.postcheckEndAt.$error ? [
                            !$v.postcheckEndAt.required && 'Complete Post Trip is required',
                            !$v.postcheckEndAt.minValue && 'Complete Post Trip has to be after Arrive Yard',
                        ] : null"
                    />
                </Column>
            </Row>
            <FormNumber
                v-model="$v.odometerPostcheck.$model"
                label="Post Trip Odometer"
                :min="0"
                :errors="$v.odometerPostcheck.$error ? [
                    !$v.odometerPostcheck.required && 'Post Trip Odometer is required',
                ] : null"
            />
        </DashboardEditSection>

        <DashboardEditSection title="Files" v-if="isEditMode && (signatureUrl || customerSignatureUrl)">
            <div v-if="isEditMode && signatureUrl" class="fileItem">
                <strong>Driver Signature</strong>
                <div class="signature">
                    <img :src="signatureUrl"/>
                </div>
            </div>
            <div v-if="isEditMode && customerSignatureUrl" class="fileItem">
                <strong>Customer Signature</strong>
                <div class="signature">
                    <img :src="customerSignatureUrl"/>
                </div>
            </div>
        </DashboardEditSection>
    </div>
</template>

<script>
import DashboardEditSection from '@/components/DashboardEditSection';
import TicketLoadList from './TicketLoadList';
import { required, minValue } from 'vuelidate/lib/validators';

export default {
    name: 'TicketForm',

    components: { DashboardEditSection, TicketLoadList },

    props: {
        isEditMode: { type: Boolean, default: false },
    },

    syncProps: [
        'id',
        'ticketNumber',
        'createdAt',
        'submittedAt',
        'client',
        'project',
        'user',
        'truck',
        'trailer',
        'odometerPrecheck',
        'precheckStartAt',
        'loads',
        'yardLeaveAt',
        'yardArriveAt',
        'postcheckEndAt',
        'odometerPostcheck',
        'notes',
        'signatureUrl',
        'customerSignatureUrl',
    ],

    validations: {
        submittedAt: { required },
        project: { required },
        user: { required },
        truck: { required },
        odometerPrecheck: { required },
        odometerPostcheck: { required },
        precheckStartAt: { required },
        yardLeaveAt: {
            required,
            minValue(value) {
                return value >= this.precheckStartAt;
            },
        },
        yardArriveAt: {
            required,
            minValue(value) {
                return value >= this.yardLeaveAt;
            },
        },
        postcheckEndAt: {
            required,
            minValue(value) {
                return value >= this.yardArriveAt;
            },
        },
    },

    methods: {
        validate() {
            this.$v.$touch();
            return !this.$v.$invalid;
        },
    },
}
</script>

<style lang="scss" scoped>
@import 'globals.scss';

.TicketForm {
    .fileItem + .fileItem{
        margin-top: 30px ;
    }

    .signature {
        @include aspect-ratio(16, 9);
        max-width: 300px;
        border-radius: $border-radius;
        border: 1px solid $divider-color;

        img {
            @include overlay();
        }
    }
}
</style>
